import React from 'react';
import { ChevronLeft, ChevronRight, Check, AlertCircle, Loader2, SkipForward } from 'lucide-react';
import type { SaveState } from './usePairwiseAutosave';
import type { Verdict } from './api';
import { VERDICT_OPTIONS, MIN_REMARKS_LENGTH, RATING_CONTENT_MAX_WIDTH } from './constants';

interface VerdictBarProps {
  verdict: Verdict | null;
  remarks: string;
  onVerdictChange: (value: Verdict) => void;
  onRemarksChange: (value: string) => void;
  onPrev: () => void;
  onNext: () => void;
  onSkip: () => void;
  canGoPrev: boolean;
  isLast: boolean;
  busy: boolean;
  saveState: SaveState;
  error: string | null;
}

function SaveIndicator({ saveState, error }: { saveState: SaveState; error: string | null }) {
  if (saveState === 'saving') {
    return (
      <span className="flex items-center gap-1 text-xs text-gray-500">
        <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> Saving…
      </span>
    );
  }
  if (saveState === 'saved') {
    return (
      <span className="flex items-center gap-1 text-xs font-semibold text-blue-700">
        <Check className="w-3 h-3" aria-hidden="true" /> Saved
      </span>
    );
  }
  if (saveState === 'error') {
    return (
      <span className="flex items-center gap-1 text-xs font-semibold text-red-600" role="alert">
        <AlertCircle className="w-3 h-3" aria-hidden="true" /> {error ?? 'Not saved'}
      </span>
    );
  }
  return null;
}

const VerdictBar: React.FC<VerdictBarProps> = ({
  verdict, remarks, onVerdictChange, onRemarksChange,
  onPrev, onNext, onSkip, canGoPrev, isLast, busy, saveState, error,
}) => {
  const remarksTooShort = remarks.trim().length < MIN_REMARKS_LENGTH;
  // Skip is offered only when NO winner is chosen but a reason IS written. Picking a side means
  // it's a verdict, not a skip — keeping the two actions unambiguous.
  const canSkip = verdict === null && !remarksTooShort;

  return (
    <div className="fixed bottom-0 left-0 right-0 md:left-16 z-40 bg-white/97 backdrop-blur
                    border-t border-gray-200 shadow-[0_-6px_22px_rgba(15,30,28,0.07)]">
      <div className={`${RATING_CONTENT_MAX_WIDTH} mx-auto px-4 md:px-8 py-3
                      flex flex-col md:flex-row md:items-start gap-4 md:gap-5`}>
        <fieldset className="shrink-0">
          <legend className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
            Your verdict
          </legend>
          <div className="flex gap-1.5 flex-wrap" role="radiogroup" aria-label="Which report is better">
            {VERDICT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={verdict === option.value}
                title={option.hint}
                onClick={() => onVerdictChange(option.value)}
                disabled={busy}
                className={`px-4 py-2 min-h-[44px] rounded-xl border-[1.5px] text-[13px]
                            font-semibold transition-all hover:-translate-y-px whitespace-nowrap
                            focus:outline-none disabled:opacity-60 focus:ring-2
                            focus:ring-blue-500 focus:ring-offset-1 ${
                  verdict === option.value
                    ? 'bg-gray-900 border-gray-900 text-white'
                    : 'bg-white border-gray-200 text-gray-500 hover:border-blue-600'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="flex-1 min-w-0">
          <label
            htmlFor="verdict-remarks"
            className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5"
          >
            Why? — or why you're skipping <span className="text-red-600">*</span>
          </label>
          <textarea
            id="verdict-remarks"
            value={remarks}
            onChange={(e) => onRemarksChange(e.target.value)}
            placeholder="What made the winner stronger — and what let the other one down…"
            className="w-full min-h-[44px] rounded-xl border-[1.5px] border-gray-200 px-3 py-2
                       text-[13px] resize-y focus:outline-none focus:border-blue-600"
            rows={2}
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Autosaves as you type · saved automatically when you press Next
          </p>
          {/* Reason-driven skip: no standalone Skip button. If the rater can't pick a winner, they
              write why here and a Skip-with-reason action appears in the bottom bar. */}
          {verdict === null && (
            <p className="text-[11px] text-gray-500 mt-1.5 flex items-start gap-1">
              <SkipForward className="w-3 h-3 mt-[1px] shrink-0" aria-hidden="true" />
              {canSkip
                ? <span>No winner picked — you can <b>Skip with reason</b> instead (button on the right). Your note is saved and this pair is closed out.</span>
                : <span>Can’t pick a winner? Write why above and a <b>Skip with reason</b> button appears — that closes the pair out (you won’t see it again).</span>}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 md:ml-auto shrink-0 flex-wrap">
          <SaveIndicator saveState={saveState} error={error} />
          {canSkip && (
            <button
              type="button"
              onClick={onSkip}
              disabled={busy}
              title="Record your reason and close this pair out — you won't see it again"
              className="px-4 py-2 min-h-[44px] rounded-xl border-[1.5px] border-gray-300 bg-white
                         text-[13px] font-semibold text-gray-700 hover:border-gray-900
                         hover:text-gray-900 disabled:opacity-50 flex items-center gap-1"
            >
              <SkipForward className="w-3.5 h-3.5" aria-hidden="true" /> Skip with reason
            </button>
          )}
          <button
            type="button"
            onClick={onPrev}
            disabled={!canGoPrev || busy}
            className="px-4 py-2 min-h-[44px] rounded-xl border-[1.5px] border-gray-200 bg-white
                       text-[13px] font-semibold disabled:opacity-40 flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" /> Prev
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={busy || verdict === null || remarksTooShort}
            title={
              verdict === null
                ? 'Pick which report is better first'
                : remarksTooShort
                  ? `Add at least ${MIN_REMARKS_LENGTH} characters explaining the choice`
                  : undefined
            }
            className="px-4 py-2 min-h-[44px] rounded-xl bg-gray-900 text-white text-[13px]
                       font-semibold hover:bg-gray-800 disabled:opacity-40
                       flex items-center gap-1"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
            {isLast ? 'Save & get next' : 'Next'}
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerdictBar;
